/**
 * ClinicalEditRail — the encounter drawer's "Edit" (Clinical Edit) tab (PROD-1262).
 *
 * Pure composition of graduated Signal DS atoms per PROD-1041 (ZERO net-new DS
 * primitives): EncounterSectionHeader (+ StatusChip count) · Banner per edit
 * (variant = severity) · Tag chips for suggested codes · EmptyState · Button.
 *
 * Faithful to Flutter `clinical_edit_tab.dart` (header + per-item card + comma-
 * split "Suggested:" chips), with the PROD-1041 improvements Flutter lacks:
 * severity → Banner.variant (Flutter uses one red flag), severity-sorted order,
 * and an explicit error state with Retry (never collapse to empty).
 *
 * Presentational only — the container (clinical-edit-tab) owns the fetch.
 * NOTE: Flutter's "Resolve" button is a dead stub (no onPressed); one-click
 * apply-to-encounter (needs the A/P write + re-eval) is deferred to a follow-up,
 * so no non-functional action button is shipped here (anti-fiction).
 */
import {
  Banner,
  Button,
  EmptyState,
  EncounterSectionHeader,
  StatusChip,
  Tag,
} from '@scripsteam/scrips-signal-ds';
import type { ClinicalEditView } from './lib/to-clinical-edit-view';

const WRAP = 'flex flex-col gap-4';
const LIST = 'flex flex-col gap-3';
const SUBTITLE = 'text-size-sm text-text-secondary mt-1';
const DESC = 'text-size-sm italic mt-2';
const SUGG_LABEL = 'text-size-xs italic text-text-secondary mt-3 mb-2';
const CHIPS = 'flex flex-wrap gap-2';
const LOADING = 'text-size-sm text-text-secondary';

export interface ClinicalEditRailProps {
  views: ClinicalEditView[];
  loading?: boolean;
  /** A failed load — rendered distinctly from "all clear", with Retry. */
  error?: boolean;
  /** The CDS engine is down — RunClinicalEdits returned 503 "checks unavailable"
   *  (PROD-1303). A degraded state: NOT a green all-clear, and NOT a hard error
   *  that blocks the encounter. Rendered as a soft warning with Retry. Takes
   *  precedence over `error` (a 503 is a known degraded mode, not a transport fault). */
  unavailable?: boolean;
  /** False until the encounter exists — suppresses the "All clear" empty state
   *  so a not-yet-run engine never reads as a clinical all-clear (Codex MODERATE). */
  ready?: boolean;
  onRetry?: () => void;
  /**
   * No title row: the host already names the block (the encounter right panel's "Clinical edits"
   * section header). Its count chip goes with it: each issue is its own banner below.
   */
  headless?: boolean;
}

export function ClinicalEditRail({ views, loading, error, unavailable, ready = true, onRetry, headless = false }: ClinicalEditRailProps) {
  const showCount = ready && !loading && !error && !unavailable && views.length > 0;
  const header = (
    <EncounterSectionHeader
      title="Clinical edits"
      actions={
        showCount
          ? [<StatusChip key="count" semantic="neutral" tone="soft">{String(views.length)}</StatusChip>]
          : undefined
      }
    />
  );

  let body;
  if (!ready) {
    // The encounter isn't open yet — the engine hasn't run. Must NOT read as "All clear".
    body = <p className={LOADING}>Clinical edits run once the encounter is ready.</p>;
  } else if (loading) {
    body = <p className={LOADING}>Checking for clinical edits…</p>;
  } else if (unavailable) {
    // The CDS engine is down (RunClinicalEdits 503). Degraded, not all-clear and
    // not a blocking error — a soft warning the clinician can retry (PROD-1303).
    body = (
      <Banner
        variant="warning"
        title="Checks unavailable"
        action={onRetry ? <Button variant="secondary" onClick={onRetry}>Retry</Button> : undefined}
      >
        Clinical-edit checks are temporarily unavailable. This isn't an all-clear — retry once the engine is back.
      </Banner>
    );
  } else if (error) {
    // A failed evaluation must never read as "all clear".
    body = (
      <Banner
        variant="error"
        title="Couldn't run clinical edits"
        action={onRetry ? <Button variant="secondary" onClick={onRetry}>Retry</Button> : undefined}
      >
        The rule engine didn't respond. Retry to re-evaluate this encounter.
      </Banner>
    );
  } else if (views.length === 0) {
    body = (
      <EmptyState
        icon="check-circle"
        title="All clear"
        description="No coding or clinical-edit issues detected for this encounter."
      />
    );
  } else {
    body = (
      <div className={LIST}>
        {views.map((v, i) => (
          <Banner key={i} variant={v.variant} title={v.title}>
            {v.subtitle && <div className={SUBTITLE}>{v.subtitle}</div>}
            {v.description && <div className={DESC}>{v.description}</div>}
            {v.suggestedCodes.length > 0 && (
              <>
                <div className={SUGG_LABEL}>Suggested:</div>
                <div className={CHIPS}>
                  {v.suggestedCodes.map((code, j) => (
                    <Tag key={j}>{code}</Tag>
                  ))}
                </div>
              </>
            )}
          </Banner>
        ))}
      </div>
    );
  }

  return (
    <div className={WRAP}>
      {headless ? null : header}
      {body}
    </div>
  );
}
